import { useThrottle } from "#/hooks/useThrottle";
import { useMarket } from "#/store/app/portfolio/market";
import { useOption } from "#/store/options/tradePanel/option";
import { usePositions } from "#/store/wallet/account/positions";
import { Market } from "#/types/markets";
import { Option } from "#/types/options";
import { RiskPosition, UserPortfolio } from "#/types/positions";
import { isOptionInstrument } from "#/utils/instrument.utils";

const setupNetting = (positions: RiskPosition[]): RiskPosition[] => {
  const nettedPositions = positions.reduce<Record<string, RiskPosition>>(
    (accumulator, { instrument, size }) => {
      if (accumulator[instrument]) {
        accumulator[instrument].size += size;
      } else {
        accumulator[instrument] = { instrument, size };
      }

      return accumulator;
    },
    {},
  );

  return Object.values(nettedPositions);
};

interface UseNettedUserPortfolioInputs {
  size: string;
  instrument: string;
  isBuy: boolean;
}

interface UseNettedUserPortfolioParams {
  option?: Option | null;
  market?: Market;
  inputs: UseNettedUserPortfolioInputs[];
  throttleFrequencyMs?: number;
}

export const useNettedUserPortfolio = ({
  option,
  market,
  inputs,
  throttleFrequencyMs,
}: UseNettedUserPortfolioParams): UserPortfolio => {
  const { option: currentOption } = useOption();
  const { market: currentMarket } = useMarket();

  const effectiveOption =
    option === null ? undefined : (option ?? currentOption);
  const effectiveMarket = market ?? currentMarket;

  const { positions } = usePositions(
    effectiveOption
      ? ({
          underlying: effectiveOption.underlying,
          pair: effectiveOption.marketPair,
        } as Market)
      : effectiveMarket,
  );
  const throttledPositions = useThrottle(
    positions,
    throttleFrequencyMs,
    effectiveOption?.marketPair ?? effectiveMarket.pair,
  );
  const effectivePositions = throttleFrequencyMs
    ? throttledPositions
    : positions;

  const nettedPositions = setupNetting([
    ...effectivePositions.map((position) => ({
      size: position.size,
      instrument: position.instrument,
    })),
    ...inputs.map((input) => ({
      instrument: input.instrument,
      size: input.isBuy ? parseFloat(input.size) : -parseFloat(input.size),
    })),
  ]);

  return {
    pair: effectiveOption?.marketPair ?? effectiveMarket.pair,
    positions: nettedPositions,
  };
};

export const useNettedOptionUserPortfolio = ({
  option,
  market,
  inputs,
}: UseNettedUserPortfolioParams): RiskPosition[] | RiskPosition | undefined => {
  const { positions } = useNettedUserPortfolio({
    option,
    market,
    inputs,
  });
  const nettedOptionPortfolio = positions.filter(({ instrument }) =>
    isOptionInstrument(instrument),
  );

  return option !== null
    ? nettedOptionPortfolio.find(
        ({ instrument }) => instrument === option?.instrument_name,
      )
    : nettedOptionPortfolio;
};
